import { FirebaseFirestore } from "@capacitor-firebase/firestore";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  writeBatch,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";


import { db } from "../firebase";
import { uploadImageData } from "./storageDataService";
import {
  isNativeFirestorePlatform,
  extractNativeDocumentData,
  extractNativeDocuments,
  removeNativeSnapshotListener,
} from "./firestorePlatform";

const FORUM_COLLECTION = "forumPosts";

const forumPostRefPath = (postId) => `${FORUM_COLLECTION}/${postId}`;
const forumRepliesRefPath = (postId) => `${FORUM_COLLECTION}/${postId}/replies`;
const forumReplyDocRefPath = (postId, replyId) =>
  `${FORUM_COLLECTION}/${postId}/replies/${replyId}`;

const normalizeObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const normalizePost = (id, data = {}) => {
  const p = normalizeObject(data);

  const subscribersA = Array.isArray(p.subscribers) ? p.subscribers : [];
  const subscribersB = Array.isArray(p.subscribedBy) ? p.subscribedBy : [];
  const subscribersUnion = Array.from(new Set([...subscribersA, ...subscribersB]));

  return {
    id,
    ...p,
    subscribers: subscribersUnion,
    subscribedBy: subscribersUnion,
    images: Array.isArray(p.images) ? p.images : [],
    title: p.title || "",
    content: p.content || "",
    category: p.category || "",
    authorId: p.authorId || p.userId || "",
    userId: p.userId || p.authorId || "",
    authorName: p.authorName || p.author || "Unbekannt",
  };
};

const normalizeReply = (id, data = {}) => {
  const r = normalizeObject(data);

  return {
    id,
    ...r,
    content: r.content || "",
    authorId: r.authorId || r.userId || "",
    authorName: r.authorName || r.author || "Unbekannt",
    images: Array.isArray(r.images) ? r.images : [],
    createdAt: r.createdAt || null,
    createdAtClient: r.createdAtClient || null,
  };
};

const extractNativeCollectionDocs = (payload) => {
  const docs = extractNativeDocuments(payload);
  return Array.isArray(docs) ? docs : [];
};

const mapNativePostCollection = (payload) => {
  const docs = extractNativeCollectionDocs(payload);

  return docs.map((item, index) => {
    const id =
      item?.id ||
      item?.documentId ||
      item?.snapshot?.id ||
      item?.document?.id ||
      (typeof item?.reference === "string" ? item.reference.split("/").pop() : null) ||
      `post-${index}`;

    const data =
      item?.data ||
      item?.snapshot?.data ||
      item?.document?.data ||
      item?.value ||
      item?.fields ||
      {};

    return normalizePost(id, data);
  });
};

const mapNativeReplyCollection = (payload) => {
  const docs = extractNativeCollectionDocs(payload);

  return docs.map((item, index) => {
    const id =
      item?.id ||
      item?.documentId ||
      item?.snapshot?.id ||
      item?.document?.id ||
      (typeof item?.reference === "string" ? item.reference.split("/").pop() : null) ||
      `reply-${index}`;

    const data =
      item?.data ||
      item?.snapshot?.data ||
      item?.document?.data ||
      item?.value ||
      item?.fields ||
      {};

    return normalizeReply(id, data);
  });
};

const toMillisSafe = (reply) => {
  if (reply?.createdAt && typeof reply.createdAt.toMillis === "function") {
    return reply.createdAt.toMillis();
  }

  if (reply?.createdAtClient && typeof reply.createdAtClient.toMillis === "function") {
    return reply.createdAtClient.toMillis();
  }

  if (typeof reply?.createdAtClient === "string") {
    const n = Date.parse(reply.createdAtClient);
    return Number.isFinite(n) ? n : 0;
  }

  if (typeof reply?.createdAt === "string") {
    const n = Date.parse(reply.createdAt);
    return Number.isFinite(n) ? n : 0;
  }

  return 0;
};
const parseForumTimeValue = (value) => {
  if (!value) return 0;

  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  if (typeof value.seconds === "number") {
    return value.seconds * 1000;
  }

  return 0;
};

const toPostActivityMillis = (post) => {
  if (!post) return 0;

  const activityCandidates = [
    post.lastActivityClientMs,
    post.lastActivityAt,
    post.lastReplyAt,
    post.lastActivityAtClient,
    post.lastReplyAtClient,
  ];

  const latestActivity = Math.max(
    ...activityCandidates.map(parseForumTimeValue)
  );

  if (latestActivity > 0) {
    return latestActivity;
  }

  const fallbackCandidates = [
    post.createdAt,
    post.createdAtClient,
  ];

  return Math.max(
    ...fallbackCandidates.map(parseForumTimeValue)
  );
};

const buildForumPayload = (data = {}) => {
  const payload = {
    ...normalizeObject(data),
  };

  if (isNativeFirestorePlatform()) {
    payload.updatedAtClient = new Date().toISOString();
    return payload;
  }

  payload.updatedAt = serverTimestamp();
  return payload;
};

const buildCreateForumPayload = (data = {}) => {
  const payload = {
    ...normalizeObject(data),
  };

  if (isNativeFirestorePlatform()) {
    payload.createdAtClient = new Date().toISOString();
    payload.updatedAtClient = new Date().toISOString();
    return payload;
  }

  payload.createdAt = serverTimestamp();
  payload.updatedAt = serverTimestamp();
  return payload;
};

const getLatestReplyMetaFromList = (replies = []) => {
  let latestTs = 0;
  let latestAuthorId = "";

  replies.forEach((reply) => {
    const ts = toMillisSafe(reply);
    if (ts > latestTs) {
      latestTs = ts;
      latestAuthorId = String(reply?.authorId || reply?.userId || "").trim();
    }
  });

  return { latestTs, latestAuthorId };
};

export const uploadForumImagesData = async (files, pathPrefix = "forumUploads") => {
  const input = Array.isArray(files) ? files : [];
  const urls = [];

  for (const file of input) {
    if (!(file instanceof File)) continue;

    const safeName = String(file.name || "file.jpg").replace(/\s+/g, "_");
    const path = `${pathPrefix}/${Date.now()}-${safeName}`;

    console.log("🟢 Forum-Bild Upload starte:", {
      path,
      name: file.name,
      type: file.type,
      size: file.size,
      nativePath: file.nativePath || null,
    });

    const url = await uploadImageData(file, path);
    urls.push(url);
  }

  return urls;
};

export const getForumPostByIdData = async (postId) => {
  if (!postId) return null;

  if (isNativeFirestorePlatform()) {
    const result = await FirebaseFirestore.getDocument({
      reference: forumPostRefPath(postId),
    });

    const data = extractNativeDocumentData(result);
    if (!data || Object.keys(data).length === 0) return null;

    return normalizePost(postId, data);
  }

  const snap = await getDoc(doc(db, FORUM_COLLECTION, postId));
  if (!snap.exists()) return null;

  return normalizePost(snap.id, snap.data());
};

export const getForumRepliesData = async (postId) => {
  if (!postId) return [];

  let replies = [];

  if (isNativeFirestorePlatform()) {
    const result = await FirebaseFirestore.getCollection({
      reference: forumRepliesRefPath(postId),
    });

    replies = mapNativeReplyCollection(result);
  } else {
    const snap = await getDocs(collection(db, FORUM_COLLECTION, postId, "replies"));
    replies = snap.docs.map((d) => normalizeReply(d.id, d.data()));
  }

  return replies.sort((a, b) => toMillisSafe(a) - toMillisSafe(b));
};

export const getForumPostWithRepliesData = async (postId) => {
  const [post, replies] = await Promise.all([
    getForumPostByIdData(postId),
    getForumRepliesData(postId),
  ]);

  if (!post) return null;

  return {
    ...post,
    replies,
  };
};

export const getForumPostsData = async () => {
  if (isNativeFirestorePlatform()) {
    const result = await FirebaseFirestore.getCollection({
      reference: FORUM_COLLECTION,
    });

    return mapNativePostCollection(result).sort(
  (a, b) => toPostActivityMillis(b) - toPostActivityMillis(a)
);
  }

  const snap = await getDocs(collection(db, FORUM_COLLECTION));
  return snap.docs
  .map((d) => normalizePost(d.id, d.data()))
  .sort((a, b) => toPostActivityMillis(b) - toPostActivityMillis(a));
};

export const getMyForumPostsData = async (myUid) => {
  if (!myUid) return [];

  if (isNativeFirestorePlatform()) {
    const all = await getForumPostsData();
    return all.filter((post) => String(post.userId || post.authorId || "") === String(myUid));
  }

  const qMine = query(collection(db, FORUM_COLLECTION), where("userId", "==", myUid));
const snap = await getDocs(qMine);

return snap.docs
  .map((d) => normalizePost(d.id, d.data()))
  .sort((a, b) => toPostActivityMillis(b) - toPostActivityMillis(a));
};

export const getMySubscribedForumPostsData = async (myUid) => {
  if (!myUid) return [];

  if (isNativeFirestorePlatform()) {
    const all = await getForumPostsData();
    return all.filter((post) => {
  const subscribers = Array.isArray(post.subscribers) ? post.subscribers : [];
  const subscribedBy = Array.isArray(post.subscribedBy) ? post.subscribedBy : [];

  return subscribers.includes(myUid) || subscribedBy.includes(myUid);
});
  }

  const qA = query(collection(db, FORUM_COLLECTION), where("subscribers", "array-contains", myUid));
  const qB = query(collection(db, FORUM_COLLECTION), where("subscribedBy", "array-contains", myUid));

  const [snapA, snapB] = await Promise.all([getDocs(qA), getDocs(qB)]);

  const map = new Map();

  snapA.docs.forEach((d) => map.set(d.id, normalizePost(d.id, d.data())));
  snapB.docs.forEach((d) => map.set(d.id, normalizePost(d.id, d.data())));

  return Array.from(map.values()).sort(
  (a, b) => toPostActivityMillis(b) - toPostActivityMillis(a)
);
};

export const createForumPostData = async (postData) => {
  const nowClientIso = new Date().toISOString();
  const nowClientMs = Date.now();

  const payload = buildCreateForumPayload({
    ...normalizeObject(postData),
    subscribers: Array.isArray(postData?.subscribers) ? postData.subscribers : [],
    subscribedBy: Array.isArray(postData?.subscribedBy) ? postData.subscribedBy : [],
    lastActivityAtClient: nowClientIso,
    lastActivityClientMs: nowClientMs,
  });

  if (isNativeFirestorePlatform()) {
    const id = `post_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    await FirebaseFirestore.setDocument({
      reference: forumPostRefPath(id),
      data: payload,
      merge: false,
    });

    return id;
  }

  const refDoc = await addDoc(collection(db, FORUM_COLLECTION), payload);
  return refDoc.id;
};

export const updateForumPostData = async (postId, data) => {
  if (!postId) throw new Error("updateForumPostData: postId fehlt");

  const payload = buildForumPayload(data);

  if (isNativeFirestorePlatform()) {
    await FirebaseFirestore.setDocument({
      reference: forumPostRefPath(postId),
      data: payload,
      merge: true,
    });
    return;
  }

  await updateDoc(doc(db, FORUM_COLLECTION, postId), payload);
};

export const deleteForumPostData = async (postId) => {
  if (!postId) throw new Error("deleteForumPostData: postId fehlt");

  if (isNativeFirestorePlatform()) {
    const replies = await getForumRepliesData(postId);

    await Promise.all(
      replies.map((reply) =>
        FirebaseFirestore.deleteDocument({
          reference: forumReplyDocRefPath(postId, reply.id),
        })
      )
    );

    await FirebaseFirestore.deleteDocument({
      reference: forumPostRefPath(postId),
    });
    return;
  }

  const repliesSnap = await getDocs(collection(db, FORUM_COLLECTION, postId, "replies"));
  const batch = writeBatch(db);

  repliesSnap.docs.forEach((d) => {
    batch.delete(doc(db, FORUM_COLLECTION, postId, "replies", d.id));
  });

  batch.delete(doc(db, FORUM_COLLECTION, postId));
  await batch.commit();
};

export const createForumReplyData = async (postId, replyData) => {
  if (!postId) throw new Error("createForumReplyData: postId fehlt");

  const nowClientIso = new Date().toISOString();
  const nowClientMs = Date.now();

  const payload = buildCreateForumPayload({
    ...normalizeObject(replyData),
    createdAtClient: nowClientIso,
    updatedAtClient: nowClientIso,
  });

  const latestAuthorId = String(
    replyData?.authorId || replyData?.userId || ""
  ).trim();

  if (isNativeFirestorePlatform()) {
    const id = `reply_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    await FirebaseFirestore.setDocument({
      reference: forumReplyDocRefPath(postId, id),
      data: payload,
      merge: false,
    });

    await FirebaseFirestore.setDocument({
      reference: forumPostRefPath(postId),
      data: {
        lastReplyAtClient: nowClientIso,
        lastActivityAtClient: nowClientIso,
        lastActivityClientMs: nowClientMs,
        lastReplyAuthorId: latestAuthorId,
        updatedAtClient: nowClientIso,
      },
      merge: true,
    });

    return id;
  }

  const refDoc = await addDoc(
    collection(db, FORUM_COLLECTION, postId, "replies"),
    payload
  );

  await updateDoc(doc(db, FORUM_COLLECTION, postId), {
    lastReplyAt: serverTimestamp(),
    lastActivityAt: serverTimestamp(),
    lastActivityClientMs: nowClientMs,
    lastReplyAtClient: nowClientIso,
    lastActivityAtClient: nowClientIso,
    lastReplyAuthorId: latestAuthorId,
    updatedAt: serverTimestamp(),
    updatedAtClient: nowClientIso,
  });

  return refDoc.id;
};

export const updateForumReplyData = async (postId, replyId, data) => {
  if (!postId || !replyId) {
    throw new Error("updateForumReplyData: postId oder replyId fehlt");
  }

  const payload = buildForumPayload(data);

  if (isNativeFirestorePlatform()) {
    await FirebaseFirestore.setDocument({
      reference: forumReplyDocRefPath(postId, replyId),
      data: payload,
      merge: true,
    });
    return;
  }

  await updateDoc(doc(db, FORUM_COLLECTION, postId, "replies", replyId), payload);
};

export const deleteForumReplyData = async (postId, replyId) => {
  if (!postId || !replyId) {
    throw new Error("deleteForumReplyData: postId oder replyId fehlt");
  }

  if (isNativeFirestorePlatform()) {
    await FirebaseFirestore.deleteDocument({
      reference: forumReplyDocRefPath(postId, replyId),
    });
    return;
  }

  await deleteDoc(doc(db, FORUM_COLLECTION, postId, "replies", replyId));
};

export const toggleForumSubscriptionData = async (postId, myUid, shouldSubscribe) => {
  if (!postId) throw new Error("toggleForumSubscriptionData: postId fehlt");
  if (!myUid) throw new Error("toggleForumSubscriptionData: myUid fehlt");

  if (isNativeFirestorePlatform()) {
    const post = await getForumPostByIdData(postId);
    const currentSubscribers = Array.isArray(post?.subscribers) ? post.subscribers : [];

    const nextSubscribers = shouldSubscribe
      ? Array.from(new Set([...currentSubscribers, myUid]))
      : currentSubscribers.filter((uid) => uid !== myUid);

    await FirebaseFirestore.setDocument({
      reference: forumPostRefPath(postId),
      data: {
        subscribers: nextSubscribers,
        subscribedBy: nextSubscribers,
        subscribed: shouldSubscribe,
        updatedAtClient: new Date().toISOString(),
      },
      merge: true,
    });

    return nextSubscribers;
  }

  await updateDoc(doc(db, FORUM_COLLECTION, postId), {
    subscribers: shouldSubscribe ? arrayUnion(myUid) : arrayRemove(myUid),
    subscribedBy: shouldSubscribe ? arrayUnion(myUid) : arrayRemove(myUid),
    subscribed: shouldSubscribe,
    updatedAt: serverTimestamp(),
  });
};

export const markForumRepliesSeenData = async (
  postId,
  replies,
  storageKey = "forum_seen_replies_v1"
) => {
  if (!postId || !Array.isArray(replies)) return;

  const latestReplyTs = replies.reduce((max, reply) => {
    const ts = toMillisSafe(reply);
    return ts > max ? ts : max;
  }, 0);

  try {
    const current = JSON.parse(localStorage.getItem(storageKey) || "{}");
    const prev = Number(current[postId] || 0);

    if (latestReplyTs > prev) {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          ...current,
          [postId]: latestReplyTs,
        })
      );
    }
  } catch (e) {
    console.warn("markForumRepliesSeenData failed:", e);
  }
};

export const subscribeToForumPostsData = (callback, errorCallback) => {
  if (typeof callback !== "function") {
    throw new Error("subscribeToForumPostsData: callback fehlt");
  }

  if (isNativeFirestorePlatform()) {
    let active = true;
    let callbackId = null;

    FirebaseFirestore.addCollectionSnapshotListener(
      { reference: FORUM_COLLECTION },
      (event, error) => {
        if (!active) return;

        if (error) {
          if (typeof errorCallback === "function") errorCallback(error);
          return;
        }

        const posts = mapNativePostCollection(event);

        const sorted = [...posts].sort((a, b) => {
  return toPostActivityMillis(b) - toPostActivityMillis(a);
});

        callback(sorted);
      }
    )
      .then((result) => {
  callbackId = result?.callbackId || result;
})
      .catch((err) => {
        if (typeof errorCallback === "function") errorCallback(err);
      });

    return () => {
      active = false;
      if (callbackId) removeNativeSnapshotListener(callbackId);
    };
  }

  return onSnapshot(
  collection(db, FORUM_COLLECTION),
  (snap) => {
    const list = snap.docs
  .map((d) => normalizePost(d.id, d.data()))
  .sort((a, b) => toPostActivityMillis(b) - toPostActivityMillis(a));

    callback(list);
  },
  );
};


export const subscribeToForumBadgeData = (myUid, callback) => {
  if (!myUid) throw new Error("subscribeToForumBadgeData: myUid fehlt");
  if (typeof callback !== "function") {
    throw new Error("subscribeToForumBadgeData: callback fehlt");
  }
    if (isNativeFirestorePlatform()) {
    let active = true;
    let postsCallbackId = null;
    let replyListenerIds = [];

    let myRepliesMap = {};
    let abosRepliesMap = {};

    const clearNativeReplyListeners = async () => {
      const ids = [...replyListenerIds];
      replyListenerIds = [];

      await Promise.all(ids.map((id) => removeNativeSnapshotListener(id)));
    };

    const emit = () => {
      if (!active) return;
      callback({
        myReplies: myRepliesMap,
        abosReplies: abosRepliesMap,
      });
    };

    const rebuildReplyListeners = async (posts) => {
      await clearNativeReplyListeners();

      myRepliesMap = {};
      abosRepliesMap = {};

      const myPosts = posts.filter(
        (post) => String(post.userId || post.authorId || "") === String(myUid)
      );

      const subscribedPosts = posts.filter((post) => {
        const subscribers = Array.isArray(post.subscribers) ? post.subscribers : [];
        const subscribedBy = Array.isArray(post.subscribedBy) ? post.subscribedBy : [];
        return subscribers.includes(myUid) || subscribedBy.includes(myUid);
      });

      const listenToReplies = async (post, target) => {
        const callbackId = await FirebaseFirestore.addCollectionSnapshotListener(
          {
            reference: forumRepliesRefPath(post.id),
          },
          (event, error) => {
            if (!active) return;

            if (error) {
              target[post.id] = {
                postId: post.id,
                latestTs: 0,
                latestAuthorId: "",
              };
              emit();
              return;
            }

            const replies = mapNativeReplyCollection(event);
            const meta = getLatestReplyMetaFromList(replies);

            target[post.id] = {
              postId: post.id,
              latestTs: meta.latestTs,
              latestAuthorId: meta.latestAuthorId,
            };

            emit();
          }
        );

        replyListenerIds.push(callbackId?.callbackId || callbackId);
      };

      await Promise.all([
        ...myPosts.map((post) => listenToReplies(post, myRepliesMap)),
        ...subscribedPosts.map((post) => listenToReplies(post, abosRepliesMap)),
      ]);

      emit();
    };

    FirebaseFirestore.addCollectionSnapshotListener(
      {
        reference: FORUM_COLLECTION,
      },
      async (event, error) => {
        if (!active) return;

        if (error) {
          callback({
            myReplies: {},
            abosReplies: {},
          });
          return;
        }

        const posts = mapNativePostCollection(event);
        await rebuildReplyListeners(posts);
      }
    )
      .then((result) => {
  postsCallbackId = result?.callbackId || result;
})
      .catch(() => {
        callback({
          myReplies: {},
          abosReplies: {},
        });
      });

    return () => {
      active = false;

      if (postsCallbackId) {
        removeNativeSnapshotListener(postsCallbackId);
      }

      clearNativeReplyListeners();
    };
  }

  let unsubMyPosts = null;
  let unsubSubPostsA = null;
  let unsubSubPostsB = null;

  let myReplyUnsubs = [];
  let abosReplyUnsubs = [];

  let myPosts = [];
  let subscribedPosts = [];

  let myRepliesMap = {};
  let abosRepliesMap = {};

  let subscribedMapA = new Map();
  let subscribedMapB = new Map();

  const emit = () => {
    callback({
      myReplies: myRepliesMap,
      abosReplies: abosRepliesMap,
    });
  };

 const clearReplyUnsubs = (list) => {
  if (!Array.isArray(list)) return;

  list.forEach((unsub) => {
    try {
      if (typeof unsub === "function") unsub();
    } catch {}
  });
};

  const rebuildMyRepliesSubscriptions = () => {
    clearReplyUnsubs(myReplyUnsubs);
    myReplyUnsubs = [];
    myRepliesMap = {};

    if (!Array.isArray(myPosts) || myPosts.length === 0) {
      emit();
      return;
    }

    myReplyUnsubs = myPosts.map((post) =>
      onSnapshot(
        collection(db, FORUM_COLLECTION, post.id, "replies"),
        (snap) => {
          const replies = snap.docs.map((d) => normalizeReply(d.id, d.data()));
          const meta = getLatestReplyMetaFromList(replies);

          myRepliesMap = {
            ...myRepliesMap,
            [post.id]: {
              postId: post.id,
              latestTs: meta.latestTs,
              latestAuthorId: meta.latestAuthorId,
            },
          };

          emit();
        },
        () => {
          myRepliesMap = {
            ...myRepliesMap,
            [post.id]: {
              postId: post.id,
              latestTs: 0,
              latestAuthorId: "",
            },
          };

          emit();
        }
      )
    );
  };

  const rebuildAbosRepliesSubscriptions = () => {
    clearReplyUnsubs(abosReplyUnsubs);
    abosReplyUnsubs = [];
    abosRepliesMap = {};

    if (!Array.isArray(subscribedPosts) || subscribedPosts.length === 0) {
      emit();
      return;
    }

    abosReplyUnsubs = subscribedPosts.map((post) =>
      onSnapshot(
        collection(db, FORUM_COLLECTION, post.id, "replies"),
        (snap) => {
          const replies = snap.docs.map((d) => normalizeReply(d.id, d.data()));
          const meta = getLatestReplyMetaFromList(replies);

          abosRepliesMap = {
            ...abosRepliesMap,
            [post.id]: {
              postId: post.id,
              latestTs: meta.latestTs,
              latestAuthorId: meta.latestAuthorId,
            },
          };

          emit();
        },
        () => {
          abosRepliesMap = {
            ...abosRepliesMap,
            [post.id]: {
              postId: post.id,
              latestTs: 0,
              latestAuthorId: "",
            },
          };

          emit();
        }
      )
    );
  };

  const mergeSubscribedPosts = () => {
  const merged = new Map();

  subscribedMapA.forEach((val, key) => merged.set(key, val));
  subscribedMapB.forEach((val, key) => merged.set(key, val));

  const list = Array.from(merged.values()).sort(
    (a, b) => toPostActivityMillis(b) - toPostActivityMillis(a)
  );

  subscribedPosts = list;
  rebuildAbosRepliesSubscriptions();
};

  unsubMyPosts = onSnapshot(
    query(collection(db, FORUM_COLLECTION), where("userId", "==", myUid)),
    (snap) => {
      const list = snap.docs.map((d) => normalizePost(d.id, d.data()));
      myPosts = list;
      rebuildMyRepliesSubscriptions();
    },
    () => {
      myPosts = [];
      rebuildMyRepliesSubscriptions();
    }
  );

  unsubSubPostsA = onSnapshot(
    query(collection(db, FORUM_COLLECTION), where("subscribers", "array-contains", myUid)),
    (snap) => {
      const list = snap.docs.map((d) => normalizePost(d.id, d.data()));
      subscribedMapA = new Map(list.map((post) => [post.id, post]));
      mergeSubscribedPosts();
    },
    () => {
      subscribedMapA = new Map();
      mergeSubscribedPosts();
    }
  );

  unsubSubPostsB = onSnapshot(
    query(collection(db, FORUM_COLLECTION), where("subscribedBy", "array-contains", myUid)),
    (snap) => {
      const list = snap.docs.map((d) => normalizePost(d.id, d.data()));
      subscribedMapB = new Map(list.map((post) => [post.id, post]));
      mergeSubscribedPosts();
    },
    () => {
      subscribedMapB = new Map();
      mergeSubscribedPosts();
    }
  );

  return () => {
    if (typeof unsubMyPosts === "function") unsubMyPosts();
    if (typeof unsubSubPostsA === "function") unsubSubPostsA();
    if (typeof unsubSubPostsB === "function") unsubSubPostsB();

    clearReplyUnsubs(myReplyUnsubs);
    clearReplyUnsubs(abosReplyUnsubs);
  };
};